<template>
  <div class="max-w-5xl mx-auto px-4 py-8">
    <h1 class="text-2xl font-bold text-gray-800 mb-6">
      {{ (user.role === 'editor' || user.role === 'reviewer') ? 'All Submissions' : 'My Submissions' }}
    </h1>

    <!-- Status Tabs -->
    <div class="flex space-x-4 mb-6 border-b pb-2">
      <button
        v-for="tab in statusTabs"
        :key="tab"
        @click="activeTab = tab"
        :class="[
          'px-4 py-2 rounded-t',
          activeTab === tab
            ? 'bg-green-600 text-white'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        ]"
      >
        {{ tab }} ({{ getCount(tab) }})
      </button>
    </div>

    <div v-if="loading" class="text-gray-600">Loading submissions...</div>
    <div v-else-if="filteredSubmissions.length === 0" class="text-gray-500">
      No {{ activeTab.toLowerCase() }} submissions found.
    </div>

    <!-- Submissions -->
    <div v-else class="grid gap-4">
      <div
        v-for="submission in filteredSubmissions"
        :key="submission.id"
        class="border rounded-lg p-4 shadow-sm bg-white"
      >
        <div class="cursor-pointer" @click="toggleDetails(submission.id)">
          <h2 class="text-lg font-semibold text-green-700 underline">
            {{ submission.title }}
          </h2>
          <div class="text-sm text-gray-600">
            Status:
            <span
              :class="{
                'text-yellow-600': submission.status === 'pending',
                'text-green-600': submission.status === 'published',
                'text-red-600': submission.status === 'rejected'
              }"
            >
              {{ submission.status }}
            </span>
          </div>
          <div class="text-xs text-gray-500">
            Submitted on: {{ formatDate(submission.createdAt) }}
          </div>
        </div>

        <!-- Expanded Details -->
        <div v-if="expandedSubmissionId === submission.id" class="mt-4 border-t pt-3">
          <p class="text-sm text-gray-700 mb-2">
            <strong>Abstract:</strong> {{ submission.abstract }}
          </p>

          <!-- PDF Viewer -->
          <div class="relative w-full border mt-2 rounded overflow-hidden" style="height: 75vh;">
            <iframe
              :src="getDocumentViewerUrl(submission)"
              class="w-full h-full"
              frameborder="0"
            />
          </div>

          <!-- Document History Section -->
          <div v-if="getDocumentHistory(submission).length > 0" class="mt-4 border-t pt-3">
            <h3 class="text-sm font-semibold text-gray-700 mb-3">
              <strong>Document History ({{ getDocumentHistory(submission).length }} files):</strong>
            </h3>
            <div class="space-y-4">
              <div
                v-for="(document, index) in getDocumentHistory(submission)"
                :key="index"
                class="border rounded-lg p-4 bg-gray-50"
              >
                <!-- File Info Header -->
                <div class="flex items-center justify-between mb-3">
                  <div class="flex items-center space-x-3">
                    <div class="flex-shrink-0">
                      <svg class="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clip-rule="evenodd"></path>
                      </svg>
                    </div>
                    <div>
                      <p class="text-sm font-medium text-gray-900">{{ document.name }}</p>
                      <p class="text-xs text-gray-500">
                        Uploaded: {{ document.uploadedAt }}
                      </p>
                    </div>
                  </div>
                  <div class="flex space-x-2">
                    <a
                      :href="document.url"
                      target="_blank"
                      class="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-full hover:bg-blue-200 transition-colors"
                    >
                      <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                      </svg>
                      View
                    </a>
                    <a
                      :href="document.url"
                      :download="document.name"
                      class="inline-flex items-center px-3 py-1 text-xs font-medium text-green-600 bg-green-100 rounded-full hover:bg-green-200 transition-colors"
                    >
                      <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                      </svg>
                      Download
                    </a>
                  </div>
                </div>

                <!-- File Comments Section - Only for Editor or Assigned Reviewer -->
                <div v-if="canCommentOnFile(submission)" class="border-t pt-3">
                  <!-- Add Comment Form -->
                  <div class="mb-3">
                    <label class="block text-xs font-medium text-gray-700 mb-1">
                      Add Comment for {{ document.name }}:
                    </label>
                    <textarea
                      v-model="fileComments[`${submission.id}_${document.url}`]"
                      placeholder="Enter your comment about this file..."
                      class="w-full border rounded p-2 text-sm resize-none"
                      rows="3"
                    ></textarea>
                    <div class="flex justify-between items-center mt-2">
                      <span class="text-xs text-gray-500">
                        Commenting as: {{ user.role === 'editor' ? 'Editor' : 'Reviewer' }}
                      </span>
                      <button
                        @click="submitFileComment(submission, document)"
                        :disabled="!fileComments[`${submission.id}_${document.url}`]?.trim()"
                        class="px-3 py-1 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Add Comment
                      </button>
                    </div>
                  </div>
                  
                  <!-- Display Existing Comments -->
                  <!-- <div v-if="getFileComments(submission, document.url).length > 0" class="space-y-2">
                    <h4 class="text-xs font-semibold text-gray-600 border-b pb-1">
                      Comments ({{ getFileComments(submission, document.url).length }}):
                    </h4>
                    <div
                      v-for="(comment, commentIdx) in getFileComments(submission, document.url)"
                      :key="commentIdx"
                      class="bg-white rounded p-3 border-l-4 border-blue-200"
                    >
                      <div class="flex justify-between items-start mb-2">
                        <div class="flex items-center space-x-2">
                          <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                                :class="comment.by.includes('Editor') ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'">
                            {{ comment.by }}
                          </span>
                          <span class="text-xs text-gray-500">{{ comment.date }}</span>
                        </div>
                      </div>
                      <p class="text-sm text-gray-700 leading-relaxed">{{ comment.message }}</p>
                    </div>
                  </div>
                   -->
                  <!-- No Comments Message -->
                  <!-- <div v-else class="text-xs text-gray-500 italic text-center py-2">
                    No comments yet. Be the first to comment on this file.
                  </div> -->
                </div>

                <!-- Message for non-authorized users -->
                <div v-else-if="user.role === 'researcher'" class="border-t pt-3 text-center">
                  <p class="text-xs text-gray-500 italic">
                    Comments from reviewers and editors will appear here.
                  </p>
                </div>
                

                  <div v-if="getFileComments(submission, document.url).length > 0" class="space-y-2">
  <h4 class="text-xs font-semibold text-gray-600 border-b pb-1">
    Comments ({{ getFileComments(submission, document.url).length }}):
  </h4>
  <div
    v-for="(comment, commentIdx) in getFileComments(submission, document.url)"
    :key="commentIdx"
    class="bg-white rounded p-3 border-l-4 border-blue-200"
  >
    <div class="flex justify-between items-start mb-2">
      <div class="flex items-center space-x-2">
        <span
          class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
          :class="comment.by.includes('Editor') 
                     ? 'bg-purple-100 text-purple-800' 
                     : 'bg-green-100 text-green-800'"
        >
          <!-- ✅ If researcher, hide actual reviewer name -->
          {{ props.user.role === 'researcher' && !comment.by.includes('Editor') 
              ? 'Reviewer' 
              : comment.by }}
        </span>
        <span class="text-xs text-gray-500">{{ comment.date }}</span>
      </div>
    </div>
    <p class="text-sm text-gray-700 leading-relaxed">{{ comment.message }}</p>
  </div>
  <!-- Existing Comment Display -->
<div
  v-for="(comment, commentIdx) in getFileComments(submission, document.url)"
  :key="commentIdx"
  class="bg-white rounded p-3 border-l-4 border-blue-200"
>
  <div class="flex justify-between items-start mb-2">
    <div class="flex items-center space-x-2">
      <span
        class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
        :class="comment.by.includes('Editor') 
                   ? 'bg-purple-100 text-purple-800' 
                   : 'bg-green-100 text-green-800'"
      >
        {{ props.user.role === 'researcher' && !comment.by.includes('Editor') 
            ? 'Reviewer' 
            : comment.by }}
      </span>
      <span class="text-xs text-gray-500">{{ comment.date }}</span>
    </div>
  </div>
  <p class="text-sm text-gray-700 leading-relaxed">{{ comment.message }}</p>

  <!-- ✅ Replies -->
  <div v-if="comment.replies?.length" class="mt-2 space-y-2 pl-4 border-l">
    <div
      v-for="(reply, replyIdx) in comment.replies"
      :key="replyIdx"
      class="bg-gray-50 p-2 rounded text-sm"
    >
      <p class="font-medium text-gray-700">
        {{ reply.by }} ({{ reply.date }})
      </p>
      <p class="text-gray-600">{{ reply.message }}</p>
    </div>
  </div>

  <!-- ✅ Reply Form for Researcher -->
  <div v-if="props.user.role === 'researcher'" class="mt-2">
    <textarea
      v-model="replyInputs[`${submission.id}_${document.url}_${commentIdx}`]"
      placeholder="Write a reply..."
      class="w-full border rounded p-2 text-xs resize-none"
      rows="2"
    ></textarea>
    <button
      @click="submitReply(submission, document, commentIdx)"
      class="mt-1 px-2 py-1 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded"
    >
      Reply
    </button>
  </div>
</div>

</div>

                  
                  <!-- No Comments Message -->
                  <div v-else class="text-xs text-gray-500 italic text-center py-2">
                    No comments yet. Be the first to comment on this file.
                  </div>
              </div>
            </div>
          </div>

          <!-- Show Editor Comments -->
          <div v-if="submission.editorComments?.length" class="mt-4 border-t pt-2">
            <h3 class="text-sm font-semibold text-gray-700 mb-2"><b>Editor Comments:</b></h3>
            <div
              v-for="(comment, idx) in submission.editorComments"
              :key="idx"
              class="text-sm text-gray-800 mb-2"
            >
              <p><strong>{{ comment.by }}</strong> ({{ comment.date }}):</p>
              <p class="ml-2 text-gray-600">{{ comment.message }}</p>
            </div>
          </div>

          <!-- Show Reviewer Comments -->
          <div v-if="user.role === 'editor' || user.role === 'reviewer'" class="text-xs text-gray-500 mt-1">
            {{ getReviewProgress(submission) }}
          </div>
          <div v-if="submission.reviewNotes?.length" class="mt-4 border-t pt-2">
            <h3 class="text-sm font-semibold text-gray-700 mb-2"><b>Reviewer Comments:</b></h3>
            <div
              v-for="(review, idx) in submission.reviewNotes"
              :key="idx"
              class="text-sm text-gray-800 mb-2"
            >
              <p><strong>{{ review.by }}</strong> ({{ review.date }}):</p>
              <p class="ml-2 text-gray-600">{{ review.message }}</p>
            </div>
          </div>

          <!-- Edit Button: for researcher if status is pending or reviewed -->
          <div v-if="user.role === 'researcher' && (submission.status === 'pending' || submission.status === 'reviewed')" class="mt-3">
            <button
              @click="editSubmission(submission)"
              class="px-4 py-1 text-sm text-white bg-yellow-600 hover:bg-yellow-700 rounded"
            >
              Modify Submission
            </button>
            <button @click="archive(submission)" class="px-4 py-1 text-sm text-white bg-red-600">
              Archive
            </button>
          </div>

          <!-- Editor: Add Comment -->
          <div v-if="user.role === 'editor'" class="mt-3">
            <textarea
              v-model="editorComments[submission.id]"
              placeholder="Enter your comment here..."
              class="w-full border rounded p-2 text-sm mb-2"
            ></textarea>
            <button
              @click="submitEditorComment(submission)"
              class="px-4 py-1 text-sm text-white bg-indigo-600 hover:bg-indigo-700 rounded"
            >
              Add Comment
            </button>
            <button
              @click="approveByEditor(submission)"
              class="px-4 py-1 text-sm text-white bg-green-700 hover:bg-green-800 rounded"
            >
              Approve Submission
            </button>
          </div>

          <!-- Reviewer: Approve/Reject -->
          <div
            v-if="user.role === 'reviewer' && submission.status === 'pending' && isAssignedReviewer(submission)"
            class="mt-3 space-y-2"
          >
            <!-- Approve -->
            <button
              @click="toggleApproveForm(submission.id)"
              class="px-4 py-1 text-sm text-white bg-green-600 hover:bg-green-700 rounded"
            >
              Return
            </button>
            <div v-if="approveForms[submission.id]" class="mt-1">
              <textarea
                v-model="approvalNotes[submission.id]"
                placeholder="Optional note for approval"
                class="w-full border rounded p-2 text-sm"
              ></textarea>
              <button
                @click="approveSubmission(submission.id)"
                class="mt-2 px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
              >
                return this proposal
              </button>
               <button
                @click="forpub(submission.id)"
                class="mt-2 px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
              >
                for publication
              </button>
            </div>
            <!-- Reject -->
            <button
              @click="toggleRejectForm(submission.id)"
              class="ml-2 px-4 py-1 text-sm text-white bg-red-600 hover:bg-red-700 rounded"
            >
              Reject
            </button>
            <div v-if="rejectionForms[submission.id]" class="mt-1">
              <textarea
                v-model="rejectionReasons[submission.id]"
                placeholder="Enter rejection reason..."
                class="w-full border rounded p-2 text-sm"
              ></textarea>
              <button
                @click="rejectSubmission(submission.id)"
                class="mt-2 px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
              >
                Submit Rejection
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Feedback Modal -->
    <div
      v-if="modalOpen"
      class="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center"
    >
      <div class="bg-white p-6 rounded shadow-md w-full max-w-md relative">
        <h2 class="text-lg font-semibold text-gray-800 mb-2">Reviewer Feedback</h2>
        <div v-if="Array.isArray(selectedFeedback)">
          <div v-for="(feedback, idx) in selectedFeedback" :key="idx" class="mb-4">
            <p class="text-sm text-gray-700 whitespace-pre-line">
              <strong>By:</strong> {{ feedback.by }}<br />
              <strong>Date:</strong> {{ feedback.date }}<br />
              <strong>Message:</strong> {{ feedback.message }}
            </p>
          </div>
        </div>
        <div v-else class="text-sm text-gray-600">
          {{ selectedFeedback }}
        </div>
        <button
          @click="modalOpen = false"
          class="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
        >
          &times;
        </button>
      </div>
    </div>

    <!-- Archive Confirmation Modal -->
    <div
      v-if="archiveModalOpen"
      class="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center"
    >
      <div class="bg-white p-6 rounded shadow-md w-full max-w-sm relative">
        <h2 class="text-lg font-semibold text-gray-800 mb-4">Archive Submission</h2>
        <p class="text-sm text-gray-700">
          Are you sure you want to archive
          <strong>{{ submissionToArchive?.title }}</strong>?
        </p>
        <div class="mt-4 flex justify-end space-x-2">
          <button
            @click="archiveModalOpen = false"
            class="px-4 py-1 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            @click="confirmArchive"
            class="px-4 py-1 text-sm text-white bg-red-600 hover:bg-red-700 rounded"
          >
            Confirm
          </button>
        </div>
        <button
          @click="archiveModalOpen = false"
          class="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
        >
          &times;
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useRouter } from 'vue-router'
import { ref, onMounted, computed } from 'vue'
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  getDoc
} from 'firebase/firestore'
import { format } from 'date-fns'

const editorComments = ref({})
const db = getFirestore()

const props = defineProps({
  user: {
    type: Object,
    required: true
  }
})

const router = useRouter()
const submissions = ref([])
const loading = ref(true)
const expandedSubmissionId = ref(null)
const activeTab = ref('All')
const replyInputs = ref({})

const statusTabs = computed(() => {
  const tabs = ['All', 'Pending', 'Published', 'Rejected']
  if (props.user?.role === 'editor') {
    tabs.splice(2, 0, 'Reviewed') // Insert before "Published"
  }
  return tabs
})

// Forms
const approveForms = ref({})
const approvalNotes = ref({})
const rejectionForms = ref({})
const rejectionReasons = ref({})

// Modal
const modalOpen = ref(false)
const selectedFeedback = ref('')
const archiveModalOpen = ref(false)
const submissionToArchive = ref(null)

// File comments
const fileComments = ref({})

// Check if user can comment on files
const canCommentOnFile = (submission) => {
  if (props.user.role === 'editor') {
    return true
  }
  if (props.user.role === 'reviewer') {
    return isAssignedReviewer(submission)
  }
  return false
}

// Check if current user is assigned reviewer
const isAssignedReviewer = (submission) => {
  if (!Array.isArray(submission.assignedReviewers)) {
    return false
  }
  return submission.assignedReviewers.includes(props.user.email)
}

const submitReply = async (submission, document, commentIdx) => {
  const key = `${submission.id}_${document.url}_${commentIdx}`
  const reply = replyInputs.value[key]

  if (!reply || !reply.trim()) return alert('Please enter a reply.')

  const articleRef = doc(db, 'articles', submission.id)
  const snap = await getDoc(articleRef)
  const articleData = snap.data()

  const existingFileComments = articleData.fileComments || {}
  const comments = existingFileComments[document.url] || []

  if (!comments[commentIdx].replies) {
    comments[commentIdx].replies = []
  }

  comments[commentIdx].replies.push({
    by: props.user.name || 'Researcher',
    role: 'researcher',
    email: props.user.email,
    date: new Date().toLocaleString('en-PH', { timeZone: 'Asia/Manila' }),
    message: reply.trim()
  })

  await updateDoc(articleRef, { fileComments: existingFileComments })

  replyInputs.value[key] = ''
  await loadSubmissions()
}

// Get file comments for a specific document
const getFileComments = (submission, fileUrl) => {
  if (!submission.fileComments || !submission.fileComments[fileUrl]) {
    return []
  }
  return submission.fileComments[fileUrl]
}

// Submit file comment
const submitFileComment = async (submission, document) => {
  const commentKey = `${submission.id}_${document.url}`
  const comment = fileComments.value[commentKey]
  
  if (!comment || !comment.trim()) {
    alert('Please enter a comment.')
    return
  }

  // Double check permissions
  if (!canCommentOnFile(submission)) {
    alert('You are not authorized to comment on this submission.')
    return
  }

  const commentNote = {
    by: props.user.name || (props.user.role === 'editor' ? 'Editor' : 'Reviewer'),
    role: props.user.role,
    email: props.user.email,
    date: new Date().toLocaleString('en-PH', {
      timeZone: 'Asia/Manila',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true
    }),
    message: comment.trim(),
    fileName: document.name
  }

  try {
    const articleRef = doc(db, 'articles', submission.id)
    const snap = await getDoc(articleRef)
    const articleData = snap.data()
    
    // Initialize fileComments if it doesn't exist
    const existingFileComments = articleData.fileComments || {}
    
    // Initialize comments array for this file if it doesn't exist
    if (!existingFileComments[document.url]) {
      existingFileComments[document.url] = []
    }
    
    // Add the new comment
    existingFileComments[document.url].push(commentNote)

    await updateDoc(articleRef, {
      fileComments: existingFileComments
    })

    // Clear the input
    fileComments.value[commentKey] = ''
    
    // Reload submissions to show the new comment
    await loadSubmissions()
    
    // Send email notification to author
    const authorEmail = await getAuthorEmail(submission.authorId)
    if (authorEmail) {
      const subject = `New comment on your file: ${document.name}`
      const message = `Hi,\n\nA ${props.user.role} has added a comment to your file "${document.name}" in the submission "${submission.title}".\n\nComment: ${comment.trim()}\n\nThank you,\nEditorial Team`
      await sendEmailNotification(authorEmail, subject, message)
    }
    
  } catch (error) {
    console.error('Failed to add file comment:', error)
    alert('Failed to add comment. Please try again.')
  }
}
// Get document history from submission
const getDocumentHistory = (submission, account) => {
  const documents = []

  // If reviewer → only blinded manuscripts
  if (props.user.role === "reviewer") {
    if (Array.isArray(submission.blindedManuscriptUrl)) {
      documents.push(...submission.blindedManuscriptUrl)
    }
  } else {
    // Other roles → load original documents
    if (Array.isArray(submission.documentUrl)) {
      documents.push(...submission.documentUrl)
    }

    if (Array.isArray(submission.docxUrl)) {
      documents.push(...submission.docxUrl)
    }

    if (Array.isArray(submission.blindedManuscriptUrl)) {
      documents.push(...submission.blindedManuscriptUrl)
    }
  }

  // Sort by upload date (newest first)
  return documents.sort((a, b) => {
    const dateA = new Date(a.uploadedAt)
    const dateB = new Date(b.uploadedAt)
    return dateB - dateA
  })
}


const archive = (submission) => {
  submissionToArchive.value = submission
  archiveModalOpen.value = true
}

const submitEditorComment = async (submission) => {
  const comment = editorComments.value[submission.id]
  if (!comment || !comment.trim()) {
    alert('Please enter a comment.')
    return
  }

  const commentNote = {
    by: props.user.name || 'Editor',
    date: new Date().toLocaleString('en-PH', {
      timeZone: 'Asia/Manila',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true
    }),
    message: comment.trim()
  }

  try {
    const articleRef = doc(db, 'articles', submission.id)
    const snap = await getDoc(articleRef)
    const articleData = snap.data()
    const existingComments = articleData.editorComments || []

    await updateDoc(articleRef, {
      editorComments: [...existingComments, commentNote]
    })

    editorComments.value[submission.id] = ''
    await loadSubmissions()
  } catch (error) {
    console.error('Failed to add comment:', error)
  }
}

const approveByEditor = async (submission) => {
  const comment = editorComments.value[submission.id] || ''
  const commentNote = {
    by: props.user.name || 'Editor',
    date: new Date().toLocaleString('en-PH', {
      timeZone: 'Asia/Manila',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true
    }),
    message: comment.trim()
  }

  try {
    const articleRef = doc(db, 'articles', submission.id)
    const snap = await getDoc(articleRef)
    const articleData = snap.data()
    const existingComments = articleData.editorComments || []

    await updateDoc(articleRef, {
      status: 'approved',
      approvedAt: new Date(),
      editorComments: [...existingComments, commentNote]
    })

    editorComments.value[submission.id] = ''
    await loadSubmissions()

    const authorEmail = await getAuthorEmail(submission.authorId)
    if (authorEmail) {
      const subject = 'Your submission has been approved for publishing'
      const message = `Hi,\n\nYour article titled "${submission.title}" has been approved and is now marked as Published.\n\nThank you,\nEditorial Team`
      await sendEmailNotification(authorEmail, subject, message)
    }
  } catch (error) {
    console.error('Editor approval failed:', error)
  }
}

const confirmArchive = async () => {
  if (!submissionToArchive.value) return

  try {
    const articleRef = doc(db, 'articles', submissionToArchive.value.id)
    await updateDoc(articleRef, {
      status: 'archived',
      archivedAt: new Date()
    })

    archiveModalOpen.value = false
    submissionToArchive.value = null
    await loadSubmissions()
  } catch (error) {
    console.error('Archiving failed:', error)
  }
}

const editSubmission = (submission) => {
  // Store data temporarily (e.g. in sessionStorage or via a global store)
  sessionStorage.setItem('editSubmission', JSON.stringify(submission))
  // Navigate to /submit
  router.push(`/edit/${submission.id}`)
}

const getDocumentViewerUrl = (submission) => {
  let fileArray = []

  if (props.user.role === 'reviewer') {
    fileArray = submission.blindedManuscriptUrl || []
  } else {
    fileArray = submission.documentUrl || submission.docxUrl || []
  }

  if (!Array.isArray(fileArray) || fileArray.length === 0) return 'about:blank'

  const file = fileArray[0] // use the first uploaded file
  const url = file?.url

  if (typeof url !== 'string') return 'about:blank'

  if (url.endsWith('.pdf')) return url
  if (url.endsWith('.docx')) {
    return `https://docs.google.com/gview?url=${encodeURIComponent(url)}&embedded=true`
  }

  return url
}

// Format date
const formatDate = (date) => {
  try {
    const d = date.toDate ? date.toDate() : new Date(date)
    return format(d, 'MMMM dd, yyyy')
  } catch {
    return '-'
  }
}

// Toggle expanded
const toggleDetails = (id) => {
  expandedSubmissionId.value = expandedSubmissionId.value === id ? null : id
}

// Load submissions
const loadSubmissions = async () => {
  loading.value = true
  try {
    const articleRef = collection(db, 'articles')
    let q

    if (props.user.role === 'editor') {
      q = query(articleRef)
    } else if (props.user.role === 'reviewer') {
      // Get all articles and filter client-side based on assignedReviewers
      q = query(articleRef)
    } else {
      q = query(articleRef, where('authorId', '==', props.user.uid))
    }

    const querySnapshot = await getDocs(q)
    let allSubmissions = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))

    // Filter on reviewer side
    if (props.user.role === 'reviewer') {
      const reviewerEmail = props.user.email
      allSubmissions = allSubmissions.filter(article =>
        Array.isArray(article.assignedReviewers) &&
        article.assignedReviewers.includes(reviewerEmail)
      )
    }

    submissions.value = allSubmissions
  } catch (error) {
    console.error('Failed to load submissions:', error)
  } finally {
    loading.value = false
  }
}

// Computed filter
const filteredSubmissions = computed(() => {
  if (activeTab.value === 'All') return submissions.value
  return submissions.value.filter(sub => sub.status === activeTab.value.toLowerCase())
})

// Count by tab
const getCount = (tab) => {
  if (tab === 'All') return submissions.value.length
  return submissions.value.filter(sub => sub.status === tab.toLowerCase()).length
}

// Feedback modal
const showFeedback = (submission) => {
  selectedFeedback.value = submission.reviewNotes || 'No feedback provided.'
  modalOpen.value = true
}

const getAuthorEmail = async (authorId) => {
  try {
    const userRef = doc(db, 'users', authorId)
    const userSnap = await getDoc(userRef)
    if (userSnap.exists()) {
      const userData = userSnap.data()
      console.log(`📧 Author Email: ${userData.email}`)
      return userData.email
    } else {
      console.warn(`No user found for ID: ${authorId}`)
      return null
    }
  } catch (err) {
    console.error('Failed to fetch author email:', err)
    return null
  }
}

const sendEmailNotification = async (to, subject, message) => {
  try {
    const response = await fetch('https://daluyangdunong.minsu.edu.ph/api/send-feedback.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ to, subject, message })
    })

    const result = await response.json()
    if (!result.success) {
      console.error('Email send failed:', result.error)
    }
  } catch (error) {
    console.error('Failed to send email:', error)
  }
}

// Get reviewer progress
const getReviewProgress = (submission) => {
  const total = Array.isArray(submission.assignedReviewers) ? submission.assignedReviewers.length : 0
  const done = Array.isArray(submission.reviewedBy) ? submission.reviewedBy.length : 0
  return `${done}/${total} reviews submitted`
}

// for publication
const forpub = async (id) => {
  try {
    const articleRef = doc(db, 'articles', id)

    await updateDoc(articleRef, {
      status: 'reviewed'
    })

    approveForms.value[id] = false
    await loadSubmissions()
  } catch (error) {
    console.error('Approval failed:', error)
  }
}

// Approve
const approveSubmission = async (id) => {
  const note = approvalNotes.value[id]?.trim() || 'Approved'
  const editorName = props.user.name || 'Editor'
  const now = new Date()

  const reviewNote = {
    by: editorName,
    date: now.toLocaleString('en-PH', {
      timeZone: 'Asia/Manila',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true
    }),
    message: note
  }

  try {
    const articleRef = doc(db, 'articles', id)
    const snap = await getDoc(articleRef)
    const existingReviewer = snap.exists ? snap.data().reviewedBy || [] : []
    const articleData = snap.data()

    await updateDoc(articleRef, {
      reviewedBy: [...existingReviewer, props.user.email],
      reviewNotes: [...(articleData.reviewNotes || []), reviewNote],
      reviewedAt: now
    })

    approveForms.value[id] = false
    await loadSubmissions()

    const authorEmail = await getAuthorEmail(articleData.authorId)
    if (authorEmail) {
      const subject = 'Your submission has been reviewed'
      const message = `Hi,\n\nYour article titled "${articleData.title}" has been reviewed.\n\nReviewer Note: ${note}\n\nThank you,\nEditorial Team`
      await sendEmailNotification(authorEmail, subject, message)
    }
  } catch (error) {
    console.error('Approval failed:', error)
  }
}

// Reject
const rejectSubmission = async (id) => {
  const reason = rejectionReasons.value[id]
  if (!reason || !reason.trim()) {
    alert('Please enter a reason for rejection.')
    return
  }

  try {
    const articleRef = doc(db, 'articles', id)
    const snap = await getDoc(articleRef)
    const articleData = snap.data()

    await updateDoc(articleRef, {
      status: 'rejected',
      rejectionReason: reason.trim(),
      reviewedAt: new Date()
    })

    rejectionForms.value[id] = false
    await loadSubmissions()

    const authorEmail = await getAuthorEmail(articleData.authorId)
    if (authorEmail) {
      const subject = 'Your submission has been rejected'
      const message = `Hi,\n\nYour article titled "${articleData.title}" has been rejected.\n\nReason: ${reason.trim()}\n\nThank you,\nEditorial Team`
      await sendEmailNotification(authorEmail, subject, message)
    }
  } catch (error) {
    console.error('Rejection failed:', error)
  }
}

const toggleRejectForm = (id) => {
  rejectionForms.value[id] = !rejectionForms.value[id]
  if (!rejectionReasons.value[id]) rejectionReasons.value[id] = ''
}

const toggleApproveForm = (id) => {
  approveForms.value[id] = !approveForms.value[id]
  if (!approvalNotes.value[id]) approvalNotes.value[id] = ''
}

onMounted(() => {
  if (props.user?.uid) loadSubmissions()
})
</script>
